import type { IObservableArray } from 'mobx';
import type { JSX } from 'react';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import type { Campaign, DisplayCreative, TChannel } from '@feathr/blackbox';
import { Button, Tooltip } from '@feathr/components';
import { useStore } from '@feathr/extender/state';
import { getIconForAction } from '@feathr/hooks';

import AddAdTagCreative from '../AddAdTagCreative';
import AddBannersnackBuildFlow from '../AddBannersnackBuildFlow';
import AddCreativeFlow from '../AddCreativeFlow';
import AddImageVideoFlow from '../AddImageVideoFlow';
import DuplicateCreativeModal from '../DuplicateCreativeModal';
import type { TCreativeType } from '../types';
import { useAddCreativeButton } from './AddCreativeButton.hooks';

export interface IAddCreativeButtonProps {
  /** Button theme type */
  type?: 'primary' | 'secondary';
  /** Callback when the creative flow completes */
  onComplete?: (channel: TChannel, creativeType: TCreativeType) => void;
  /** Campaign for click-through tracking in ad preview. */
  campaign?: Campaign;
  /** Campaign ID for duplicating creatives */
  campaignId?: string;
  /** Event ID for duplicating creatives */
  eventId?: string;
  /** Observable array of creatives for optimistic updates */
  creatives?: IObservableArray<DisplayCreative>;
  /** Callback to trigger appear animation after adding to list */
  onAppear?: (id: string) => void;
  /** Available channels based on campaign selection. If empty, button is disabled. */
  availableChannels?: TChannel[];
}

function AddCreativeButton({
  type = 'primary',
  onComplete,
  campaign,
  campaignId,
  eventId,
  creatives,
  onAppear,
  availableChannels,
}: Readonly<IAddCreativeButtonProps>): JSX.Element {
  const { t } = useTranslation();
  const { Creatives } = useStore();

  const {
    modalState,
    isAddCreativeFlowOpen,
    isDuplicateModalOpen,
    isImageVideoOpen,
    imageVideoChannel,
    isBannersnackBuildOpen,
    isAdTagCreativeOpen,
    handleOpenAddCreativeFlow,
    handleCloseAll,
    handleAddCreativeFlowComplete,
    handleDuplicateModalBack,
    handleImageVideoBack,
    handleBannersnackBuildBack,
    handleAdTagCreativeBack,
  } = useAddCreativeButton({ onComplete });

  const handleComplete = useCallback((): void => {
    Creatives.refreshApiCache();
    handleCloseAll();
  }, [Creatives, handleCloseAll]);

  const isButtonDisabled = availableChannels?.length === 0;
  const disabledTooltip = isButtonDisabled ? t('Select a channel first.') : undefined;

  const button = (
    <Button
      disabled={isButtonDisabled}
      onClick={handleOpenAddCreativeFlow}
      prefix={getIconForAction('add')}
      type={type}
    >
      {t('Add creative')}
    </Button>
  );

  return (
    <>
      {disabledTooltip ? <Tooltip title={disabledTooltip}>{button}</Tooltip> : button}
      {isAddCreativeFlowOpen && (
        <AddCreativeFlow
          availableChannels={availableChannels}
          campaignId={campaignId}
          eventId={eventId}
          initialChannel={modalState.modal === 'addCreativeFlow' ? modalState.channel : undefined}
          initialStep={
            modalState.modal === 'addCreativeFlow' && modalState.returnToStep2
              ? 'creative-type'
              : 'channel'
          }
          isOpen={true}
          onClose={handleCloseAll}
          onComplete={handleAddCreativeFlowComplete}
        />
      )}
      {isDuplicateModalOpen && modalState.modal === 'duplicateCreative' && (
        <DuplicateCreativeModal
          campaignId={modalState.campaignId ?? campaignId}
          channel={modalState.channel}
          creatives={creatives}
          eventId={eventId}
          isOpen={true}
          onAppear={onAppear}
          onBack={handleDuplicateModalBack}
          onClose={handleCloseAll}
          onSuccess={handleComplete}
        />
      )}
      {isImageVideoOpen && modalState.modal === 'imageVideo' && imageVideoChannel && (
        <AddImageVideoFlow
          campaign={campaign}
          campaignId={modalState.campaignId ?? campaignId}
          channel={imageVideoChannel}
          creatives={creatives}
          eventId={eventId}
          onAppear={onAppear}
          onBack={handleImageVideoBack}
          onCancel={handleCloseAll}
          onComplete={handleComplete}
        />
      )}
      {isBannersnackBuildOpen && modalState.modal === 'bannersnackBuild' && (
        <AddBannersnackBuildFlow
          campaign={campaign}
          campaignId={modalState.campaignId ?? campaignId}
          creatives={creatives}
          eventId={eventId}
          isOpen={true}
          onAppear={onAppear}
          onBack={handleBannersnackBuildBack}
          onCancel={handleCloseAll}
          onComplete={handleComplete}
        />
      )}
      {isAdTagCreativeOpen && modalState.modal === 'adTagCreative' && (
        <AddAdTagCreative
          campaign={campaign}
          campaignId={modalState.campaignId ?? campaignId}
          creatives={creatives}
          eventId={eventId}
          isOpen={true}
          onAppear={onAppear}
          onBack={handleAdTagCreativeBack}
          onCancel={handleCloseAll}
          onComplete={handleComplete}
        />
      )}
    </>
  );
}

export default AddCreativeButton;
